'use client'

/**
 * ExportDropdown - Export button with PDF option
 *
 * Export flow with loading states and toast notifications.
 * Uses dynamic imports to keep PDF libraries out of main bundle (bundle-dynamic-imports).
 * Premium gating follows architecture-avoid-boolean-props and patterns-explicit-variants.
 */
import React from 'react'

import { Download, FileText } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import Spinner from '@/components/common/Spinner'
import { PremiumAccessPopover } from '@/components/home/ui/PremiumAccessPopover'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useRecipientPdfExport } from '@/hooks/usaspending'

import { useRecipientDetail } from '../context'

interface ExportButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
}

/**
 * Export button component with forwarded ref for DropdownMenuTrigger (asChild pattern).
 * Note: forwardRef is required in React 18 for Radix asChild to work correctly.
 * Uses twMerge to preserve base styles when Radix passes className via asChild.
 */
const ExportButton = React.forwardRef<HTMLButtonElement, ExportButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={twMerge(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors text-primary-600 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)
ExportButton.displayName = 'ExportButton'

export function ExportDropdown() {
  const { recipientId, chartRefs } = useRecipientDetail()
  const { exportToPdf, isExporting } = useRecipientPdfExport({
    recipientId,
    chartRefs,
  })
  const { canAccessPremium } = useCanAccessPremium()

  // Non-premium variant: button triggers premium upsell popover
  if (!canAccessPremium) {
    return (
      <PremiumAccessPopover>
        <ExportButton>
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Export</span>
        </ExportButton>
      </PremiumAccessPopover>
    )
  }

  // Premium variant: full dropdown menu with export options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ExportButton disabled={isExporting}>
          {isExporting ? (
            <>
              <Spinner className="w-4 h-4" variant="outline-compact" />
              <span className="hidden sm:inline">Exporting...</span>
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </>
          )}
        </ExportButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2">
        <div className="flex flex-col gap-1 p-3 mb-2 text-primary-900 dark:text-primary-100 rounded-[5px] border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-800/40">
          <p className="text-sm font-medium text-primary-800 dark:text-primary-200">
            Export Contractor Report
          </p>
          <p className="text-xs text-primary-600 dark:text-primary-400">
            Generate a professional PDF report with all analytics and insights.
          </p>
        </div>

        <DropdownMenuItem
          className="flex items-start gap-3 p-3 cursor-pointer"
          onSelect={exportToPdf}
          disabled={isExporting}
        >
          <div className="flex items-center justify-center mt-[2px]">
            <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-sm">PDF Document (.pdf)</span>
            <span className="text-xs text-primary-600 dark:text-primary-400">
              Professional industry report format
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Hook to access chart ref registration from section components.
 * Uses the main RecipientDetailContext.
 */
export function useExportChartRef() {
  const context = useRecipientDetail()
  return {
    registerChartRef: context.registerChartRef,
    chartRefs: context.chartRefs,
  }
}
